import { useCallback } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateDocumentMailDraftInput,
  MailDraftResult,
  MailDraftStatus,
  CreateSalesDocumentInput,
  ImportQuoteInput,
  ItemResponse,
  ListResponse,
  PaymentScheduleResponse,
  QuoteImportParseResponse,
  ReplacePaymentScheduleItemsInput,
  ReplaceSalesItemsInput,
  SalesDocumentKind,
  SalesDocumentResponse,
  SalesDocumentStatus,
  SalesSummary,
  UpdateSalesDocumentInput,
  UserOption,
} from '@proerp/shared';
import { MAIL_DRAFT_KEY } from '@/features/settings/mailDraftSettingsApi';
import { apiRequest, postAuthedFile, postAuthedMultipart } from '@/lib/apiClient';

const SALES_KEY = ['sales-documents'] as const;

export interface SalesDocumentsQuery {
  /** Mehrere Arten (z. B. Rechnungen-Tab: RE + Anzahlung + Schluss) als Array. */
  kind: SalesDocumentKind | SalesDocumentKind[];
  q?: string;
  status?: SalesDocumentStatus;
  /** Geschäftsjahr-Filter (Belegdatum); weglassen = alle Jahre. */
  year?: number;
  /** Gesellschaft, die den Beleg ausgestellt hat. */
  legalEntityId?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
}

function buildSalesQueryString(query: SalesDocumentsQuery): string {
  const params = new URLSearchParams({ kind: Array.isArray(query.kind) ? query.kind.join(',') : query.kind });
  params.set('page', String(query.page ?? 1));
  params.set('pageSize', String(query.pageSize ?? 100));
  if (query.q) params.set('q', query.q);
  if (query.status) params.set('status', query.status);
  if (query.year) params.set('year', String(query.year));
  if (query.legalEntityId) params.set('legalEntityId', query.legalEntityId);
  if (query.sort) params.set('sort', query.sort);
  return params.toString();
}

/** Nicht-Hook-Fetch einer Beleg-Seite (für „Export alles" via collectAllPages). */
export function fetchSalesDocumentsPage(query: SalesDocumentsQuery): Promise<ListResponse<SalesDocumentResponse>> {
  return apiRequest<ListResponse<SalesDocumentResponse>>(`/v1/sales-documents?${buildSalesQueryString(query)}`);
}

export function useSalesDocuments(query: SalesDocumentsQuery) {
  return useQuery({
    queryKey: [...SALES_KEY, query],
    queryFn: () => apiRequest<ListResponse<SalesDocumentResponse>>(`/v1/sales-documents?${buildSalesQueryString(query)}`),
    placeholderData: keepPreviousData,
  });
}

/**
 * Verkaufs-Kennzahlen (Umsatz laufendes Jahr, Auftragsbestand, Angebots-Trichter,
 * Belegarten, Top-Kunden) serverseitig über ALLE Belege
 * (`GET /v1/sales-documents/summary`). Ersetzt `useSalesDocumentOverview`, das
 * die neuesten 200 Belege holte, über die die Seiten dann im Browser summierten
 * (bis 2026-09-29). Unter `SALES_KEY` — Beleg-Mutationen frischen es mit auf.
 */
export function useSalesSummary() {
  return useQuery({
    queryKey: [...SALES_KEY, 'summary'],
    queryFn: () => apiRequest<ItemResponse<SalesSummary>>('/v1/sales-documents/summary'),
  });
}

/** Alle Belege eines Projekts (für den Projekt-Tab „Belege"). */
export function useProjectSalesDocuments(projectId: string, { enabled = true }: { enabled?: boolean } = {}) {
  const params = new URLSearchParams({ pageSize: '200', projectId });
  return useQuery({
    queryKey: [...SALES_KEY, { projectId }],
    queryFn: () =>
      apiRequest<ListResponse<SalesDocumentResponse>>(`/v1/sales-documents?${params.toString()}`),
    enabled: enabled && Boolean(projectId),
  });
}

/** Alle Belege eines Kunden (für den Kunden-Tab „Belege"). */
export function useCustomerSalesDocuments(customerId: string) {
  const params = new URLSearchParams({ pageSize: '200', customerId });
  return useQuery({
    queryKey: [...SALES_KEY, { customerId }],
    queryFn: () =>
      apiRequest<ListResponse<SalesDocumentResponse>>(`/v1/sales-documents?${params.toString()}`),
    enabled: Boolean(customerId),
  });
}

/** Folgebelege eines Belegs (Belegfluss). */
export function useDocumentsBySource(sourceDocumentId: string) {
  const params = new URLSearchParams({ pageSize: '50', sourceDocumentId });
  return useQuery({
    queryKey: [...SALES_KEY, { sourceDocumentId }],
    queryFn: () =>
      apiRequest<ListResponse<SalesDocumentResponse>>(`/v1/sales-documents?${params.toString()}`),
    enabled: Boolean(sourceDocumentId),
  });
}

/** Warenausgang eines Lieferscheins — bucht Lagerabgänge. */
export function useShipSalesDocument(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<ItemResponse<SalesDocumentResponse>>(`/v1/sales-documents/${id}/ship`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SALES_KEY });
      void queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
}

export function useSalesDocument(id: string) {
  return useQuery({
    queryKey: [...SALES_KEY, 'detail', id],
    queryFn: () => apiRequest<ItemResponse<SalesDocumentResponse>>(`/v1/sales-documents/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateSalesDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSalesDocumentInput) =>
      apiRequest<ItemResponse<SalesDocumentResponse>>('/v1/sales-documents', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SALES_KEY }),
  });
}

/** Angebots-PDF lesen: liefert Kopfdaten-Vorschlag + Kunden-Matches (kein Speichern). */
export function parseQuoteImportPdf(file: File): Promise<ItemResponse<QuoteImportParseResponse>> {
  return postAuthedFile<ItemResponse<QuoteImportParseResponse>>(
    '/v1/sales-documents/quote-import/parse',
    file,
  );
}

/**
 * Angebot aus PDF-Import anlegen: Kopfdaten + PDFs in EINEM multipart-Request —
 * der Server speichert die PDFs automatisch als Beleg-Anhänge (Anlagen).
 */
export function useImportQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, files }: { input: ImportQuoteInput; files: File[] }) =>
      postAuthedMultipart<ItemResponse<SalesDocumentResponse>>(
        '/v1/sales-documents/quote-import',
        input,
        files,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SALES_KEY }),
  });
}

/**
 * Schreibzugriff auf einen Beleg. Invalidiert danach Detail UND Liste.
 *
 * `invalidate: false` schaltet das ab — für den **Beleg-Editor**, der beim Speichern
 * mehrere Positionen nacheinander schreibt. Invalidiert jede einzelne Mutation, lädt
 * der Editor je Position neu und baut sich dabei über `key={adapter.revision}` neu
 * auf: die Positionen verschwinden und kommen einzeln zurück (gemeldet 2026-07-31).
 * Der Editor invalidiert stattdessen EINMAL, wenn alles geschrieben ist.
 */
export type DocMutationOptions = { invalidate?: boolean };

function useDocMutation<TVars, TResult = unknown>(
  fn: (vars: TVars) => Promise<TResult>,
  id: string,
  options: DocMutationOptions = {},
) {
  const queryClient = useQueryClient();
  const shouldInvalidate = options.invalidate ?? true;
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      if (!shouldInvalidate) return;
      void queryClient.invalidateQueries({ queryKey: [...SALES_KEY, 'detail', id] });
      void queryClient.invalidateQueries({ queryKey: SALES_KEY });
    },
  });
}

/**
 * Beleg-Queries neu holen — nachdem der Editor ALLE Positionen geschrieben hat.
 * Genau ein Neuaufbau statt einem je Position.
 */
export function useInvalidateSalesDocument(id: string) {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [...SALES_KEY, 'detail', id] });
    void queryClient.invalidateQueries({ queryKey: SALES_KEY });
  }, [queryClient, id]);
}

/**
 * Antwort des eigenen Speicherns in den Cache legen, statt neu zu laden (ADR 0115):
 * Der Editor übernimmt den Stand selbst, ein Refetch des Belegs wäre doppelte
 * Arbeit. Alles andere unter `sales-documents` (Listen, Belegfluss, Zahlungsplan)
 * wird wie bisher neu geholt.
 */
export function useStoreSalesDocument(id: string) {
  const queryClient = useQueryClient();
  return useCallback(
    (doc: SalesDocumentResponse) => {
      queryClient.setQueryData<ItemResponse<SalesDocumentResponse>>([...SALES_KEY, 'detail', id], { data: doc });
      void queryClient.invalidateQueries({
        queryKey: SALES_KEY,
        predicate: (query) => !(query.queryKey[1] === 'detail' && query.queryKey[2] === id),
      });
    },
    [queryClient, id],
  );
}

export function useUpdateSalesDocument(id: string, options?: DocMutationOptions) {
  return useDocMutation(
    (input: UpdateSalesDocumentInput) =>
      apiRequest<ItemResponse<SalesDocumentResponse>>(`/v1/sales-documents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    id,
    options,
  );
}

export function useDeleteSalesDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ data: { id: string } }>(`/v1/sales-documents/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SALES_KEY }),
  });
}

export function useConvertSalesDocument(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetKind: SalesDocumentKind) =>
      apiRequest<ItemResponse<SalesDocumentResponse>>(`/v1/sales-documents/${id}/convert`, {
        method: 'POST',
        body: JSON.stringify({ targetKind }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SALES_KEY }),
  });
}

/**
 * Beleg duplizieren: neuer Beleg derselben Art mit allen Positionen, eigener
 * Nummer und Status „Entwurf" (kein Folgebeleg — s. Backend `duplicateDocument`).
 */
export function useDuplicateSalesDocument(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<ItemResponse<SalesDocumentResponse>>(`/v1/sales-documents/${id}/duplicate`, {
        method: 'POST',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SALES_KEY }),
  });
}

/**
 * Versendete Rechnung wieder in den Entwurf holen (Tech-Lead 2026-08-05).
 * Eigenes Recht (`sales.reopeninvoice`) + Pflicht-Begründung fürs Audit-Log;
 * der Server sperrt bezahlte Rechnungen und solche mit bestehender Gutschrift.
 */
export function useReopenSalesDocument(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      apiRequest<ItemResponse<SalesDocumentResponse>>(`/v1/sales-documents/${id}/reopen`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SALES_KEY }),
  });
}

/**
 * Absender (ausstellende Gesellschaft) wechseln, wenn Umnummerieren nicht mehr
 * geht: storniert den Beleg und stellt ihn unter der neuen Gesellschaft neu aus.
 * Liefert den NEUEN Beleg zurück. Der einfache Fall (Entwurf, Nummer noch die
 * zuletzt vergebene) läuft über `useUpdateSalesDocument` mit `legalEntityId` —
 * der nummeriert um, ohne Storno.
 */
export function useChangeSalesLegalEntity(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (legalEntityId: string) =>
      apiRequest<ItemResponse<SalesDocumentResponse>>(`/v1/sales-documents/${id}/change-legal-entity`, {
        method: 'POST',
        body: JSON.stringify({ legalEntityId }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SALES_KEY }),
  });
}

/**
 * Freigabeberechtigte Personen (Vier-Augen, ADR 0062) für den Freigeber-Picker.
 * Serverseitig gefiltert auf Träger von `sales.release`.
 */
export function useSalesReleaseApprovers() {
  return useQuery({
    queryKey: [...SALES_KEY, 'release-approvers'],
    queryFn: () => apiRequest<ListResponse<UserOption>>('/v1/sales-documents/release-approvers'),
    staleTime: 5 * 60 * 1000,
  });
}

/** Vier-Augen-Freigabe anfordern (Freigeber wählen). */
export function useRequestSalesRelease(id: string) {
  return useDocMutation(
    (approverUserIds: string[]) =>
      apiRequest<ItemResponse<SalesDocumentResponse>>(`/v1/sales-documents/${id}/request-release`, {
        method: 'POST',
        body: JSON.stringify({ approverUserIds }),
      }),
    id,
  );
}

/** Beleg freigeben (Vier-Augen — nicht der eigene Beleg). */
export function useReleaseSalesDocument(id: string) {
  return useDocMutation<void>(
    () =>
      apiRequest<ItemResponse<SalesDocumentResponse>>(`/v1/sales-documents/${id}/release`, {
        method: 'POST',
      }),
    id,
  );
}

/**
 * Freigabe zurückziehen, um den Beleg ändern zu dürfen („Ändern" im
 * Freigabe-Hinweis). Danach steht der Beleg wieder ohne Freigabe da und muss
 * neu freigegeben werden.
 */
export function useWithdrawSalesRelease(id: string) {
  return useDocMutation<void>(
    () =>
      apiRequest<ItemResponse<SalesDocumentResponse>>(`/v1/sales-documents/${id}/withdraw-release`, {
        method: 'POST',
      }),
    id,
  );
}

/** Freigabe ablehnen (mit Begründung). */
export function useRejectSalesRelease(id: string) {
  return useDocMutation(
    (reason: string) =>
      apiRequest<ItemResponse<SalesDocumentResponse>>(`/v1/sales-documents/${id}/reject-release`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    id,
  );
}

/**
 * **Alle Positionen in einem Request ersetzen** — so speichert der Beleg-Editor.
 *
 * Der Aufrufer schickt den Soll-Zustand (Zeilen mit `id` bestehen weiter, Zeilen
 * ohne `id` sind neu, Fehlendes wird gelöscht); die Array-Reihenfolge ist die
 * Belegreihenfolge. Ein Beleg mit 30 Positionen brauchte vorher bis zu 31 Requests
 * nacheinander — jetzt genau einen, unabhängig von der Länge.
 */
export function useReplaceSalesItems(id: string, options?: DocMutationOptions) {
  return useDocMutation(
    (input: ReplaceSalesItemsInput) =>
      apiRequest<ItemResponse<SalesDocumentResponse>>(`/v1/sales-documents/${id}/items`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    id,
    options,
  );
}

/** Zahlungsplan eines Auftrags (ADR 0077) — lazy angelegt beim ersten Zugriff. */
export function useSalesPaymentSchedule(orderId: string) {
  return useQuery({
    queryKey: [...SALES_KEY, 'payment-schedule', orderId],
    queryFn: () =>
      apiRequest<ItemResponse<PaymentScheduleResponse>>(`/v1/sales-documents/${orderId}/payment-schedule`),
    enabled: Boolean(orderId),
  });
}

/** Ersetzt die GEPLANT-Positionen (Zeileneditor/Vorlagen, §1/§2). */
export function useReplaceSalesPaymentScheduleItems(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReplacePaymentScheduleItemsInput) =>
      apiRequest<ItemResponse<PaymentScheduleResponse>>(
        `/v1/sales-documents/${orderId}/payment-schedule/items`,
        { method: 'PUT', body: JSON.stringify(input) },
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [...SALES_KEY, 'payment-schedule', orderId] }),
  });
}

/** Rechnung aus einer Zahlungsplan-Position erzeugen (§3, Snapshot-Abzugszeilen). */
export function useCreateInvoiceFromScheduleItem(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      apiRequest<ItemResponse<SalesDocumentResponse>>(
        `/v1/sales-documents/${orderId}/payment-schedule/items/${itemId}/invoice`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...SALES_KEY, 'payment-schedule', orderId] });
      void queryClient.invalidateQueries({ queryKey: SALES_KEY });
    },
  });
}

/**
 * Mail-Entwurf über Power Automate (ADR 0113, Nachtrag): eingerichtet? und Postfach.
 * Liegt unter dem gemeinsamen Präfix, damit Speichern in den Einstellungen ihn auffrischt.
 */
export function useSalesMailDraftStatus({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...MAIL_DRAFT_KEY, 'status', 'sales'],
    queryFn: () => apiRequest<ItemResponse<MailDraftStatus>>('/v1/sales-documents/mail-draft-status'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Verkaufsbeleg als Mail-Entwurf ins Postfach (nichts wird gesendet). */
export function useCreateSalesMailDraft(id: string) {
  return useMutation({
    mutationFn: (input: CreateDocumentMailDraftInput) =>
      apiRequest<ItemResponse<MailDraftResult>>(`/v1/sales-documents/${id}/mail-draft`, { method: 'POST', body: JSON.stringify(input) }),
  });
}
